<template><div><component-combinator component-name="DtDropdown" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the Dropdown component when you have a list of links or actions that can be performed. Use the <RouteLink to="/components/select-menu.html" class="d-docsite--link d-link">Select Menu</RouteLink> component to allow the users to choose one option from the list.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The dropdown component provides accessibility controls when using with List item component.</li>
<li class="d-docsite--list-element">The dropdown has two major pieces that are provided as required named slots: the anchor and the list.</li>
<li class="d-docsite--list-element">The <strong>anchor</strong> slot contains the element that controls the dropdown's visibility.</li>
<li class="d-docsite--list-element">The <strong>list</strong> slot contains a list of items to show as dropdown menu items.</li>
</ul>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-dropdown navigation-type=&quot;arrow-keys&quot;&gt;
  &lt;template #anchor=&quot;{ attrs }&quot;&gt;
    &lt;dt-button v-bind=&quot;attrs&quot;&gt;
      Click to open
    &lt;/dt-button&gt;
  &lt;/template&gt;
  &lt;template #list=&quot;{ close }&quot;&gt;
    &lt;dt-list-item
      v-for=&quot;(item) in items&quot;
      :key=&quot;item.id&quot;
      role=&quot;menuitem&quot;
      :navigation-type=&quot;arrow-keys&quot;
      @click=&quot;close&quot;
    &gt;
      {{ item.name }}
    &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-dropdown&gt;'>
<dt-dropdown navigation-type="arrow-keys">
  <template #anchor="{ attrs }">
    <dt-button v-bind="attrs">
      Click to open
    </dt-button>
  </template>
  <template #list="{ close }">
    <dt-list-item
      v-for="(item) in items"
      :key="item.id"
      role="menuitem"
      :navigation-type="arrow-keys"
      @click="close"
    >
      {{ item.name }}
    </dt-list-item>
  </template>
</dt-dropdown>
</code-example>
<h3 id="with-sections-and-headings" tabindex="-1" class="d-docsite--header-3">With Sections and Headings <a class="header-anchor d-link d-docsite--link d-link" href="#with-sections-and-headings" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-dropdown navigation-type=&quot;arrow-keys&quot;&gt;
  &lt;template #anchor=&quot;{ attrs }&quot;&gt;
    &lt;dt-button v-bind=&quot;attrs&quot;&gt;
      Click to open
    &lt;/dt-button&gt;
  &lt;/template&gt;
  &lt;template #list=&quot;{ close }&quot;&gt;
    &lt;dt-list-item-group
      heading-class=&quot;d-py-50 d-px-100 d-c-default d-fc-tertiary d-label--sm&quot;
      heading=&quot;Menu Heading A&quot;
    &gt;
      &lt;dt-list-item
        role=&quot;menuitem&quot;
        navigation-type=&quot;arrow-keys&quot;
        @click=&quot;close&quot;
      &gt;
        Menu Item 1
      &lt;/dt-list-item&gt;
      &lt;dt-dropdown-separator /&gt;
      &lt;dt-list-item
        role=&quot;menuitem&quot;
        navigation-type=&quot;arrow-keys&quot;
        @click=&quot;close&quot;
      &gt;
        Menu Item 2
      &lt;/dt-list-item&gt;
    &lt;/dt-list-item-group&gt;
    &lt;dt-dropdown-separator /&gt;
    &lt;dt-list-item-group
      heading-class=&quot;d-py-50 d-px-100 d-c-default d-fc-tertiary d-label--sm&quot;
      heading=&quot;Menu Heading B&quot;
    &gt;
      &lt;dt-list-item
        role=&quot;menuitem&quot;
        navigation-type=&quot;arrow-keys&quot;
        @click=&quot;close&quot;
      &gt;
        Menu Item 3
      &lt;/dt-list-item&gt;
    &lt;/dt-list-item-group&gt;
  &lt;/template&gt;
&lt;/dt-dropdown&gt;'>
<dt-dropdown navigation-type="arrow-keys">
  <template #anchor="{ attrs }">
    <dt-button v-bind="attrs">
      Click to open
    </dt-button>
  </template>
  <template #list="{ close }">
    <dt-list-item-group
      heading-class="d-py-50 d-px-100 d-c-default d-fc-tertiary d-label--sm"
      heading="Menu Heading A"
    >
      <dt-list-item
        role="menuitem"
        navigation-type="arrow-keys"
        @click="close"
      >
        Menu Item 1
      </dt-list-item>
      <dt-dropdown-separator />
      <dt-list-item
        role="menuitem"
        navigation-type="arrow-keys"
        @click="close"
      >
        Menu Item 2
      </dt-list-item>
    </dt-list-item-group>
    <dt-dropdown-separator />
    <dt-list-item-group
      heading-class="d-py-50 d-px-100 d-c-default d-fc-tertiary d-label--sm"
      heading="Menu Heading B"
    >
      <dt-list-item
        role="menuitem"
        navigation-type="arrow-keys"
        @click="close"
      >
        Menu Item 3
      </dt-list-item>
    </dt-list-item-group>
  </template>
</dt-dropdown>
</code-example>
<h3 id="context-menu" tabindex="-1" class="d-docsite--header-3">Context Menu <a class="header-anchor d-link d-docsite--link d-link" href="#context-menu" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Set <code v-pre>openOnContext=true</code> to open the menu on right-click (context menu) and disable the default trigger behavior.</p>
<code-example source-code='&lt;dt-dropdown navigation-type=&quot;arrow-keys&quot; :open-on-context=&quot;true&quot;&gt;
  &lt;template #anchor=&quot;{ attrs }&quot;&gt;
    &lt;div
      v-bind=&quot;attrs&quot;
      class=&quot;d-ba d-bas-dashed d-w-400 d-py-600 d-ta-center d-bgc-black-300&quot;
    &gt;
      Right click to open
    &lt;/div&gt;
  &lt;/template&gt;
  &lt;template #list=&quot;{ close }&quot;&gt;
    &lt;dt-list-item
      v-for=&quot;(item) in items&quot;
      :key=&quot;item.id&quot;
      role=&quot;menuitem&quot;
      :navigation-type=&quot;arrow-keys&quot;
      @click=&quot;close&quot;
    &gt;
      {{ item.name }}
    &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-dropdown&gt;'>
<dt-dropdown navigation-type="arrow-keys" :open-on-context="true">
  <template #anchor="{ attrs }">
    <div
      v-bind="attrs"
      class="d-ba d-bas-dashed d-w-400 d-py-600 d-ta-center d-bgc-black-300"
    >
      Right click to open
    </div>
  </template>
  <template #list="{ close }">
    <dt-list-item
      v-for="(item) in items"
      :key="item.id"
      role="menuitem"
      :navigation-type="arrow-keys"
      @click="close"
    >
      {{ item.name }}
    </dt-list-item>
  </template>
</dt-dropdown>
</code-example>
<h2 id="content-mode" tabindex="-1" class="d-docsite--header-2">Content Mode <a class="header-anchor d-link d-docsite--link d-link" href="#content-mode" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Dropdown content renders outside the DOM tree. Use the <code v-pre>contentMode</code> prop to apply color mode (invert, light, dark) to the positioned content. See <a href="/components/mode-island.html#positioned-components" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Positioned Components</a> for details.</p>
<code-example source-code='&lt;dt-dropdown content-mode=&quot;invert&quot;&gt;...&lt;/dt-dropdown&gt;
&lt;dt-dropdown content-mode=&quot;dark&quot;&gt;...&lt;/dt-dropdown&gt;
&lt;dt-dropdown content-mode=&quot;light&quot;&gt;...&lt;/dt-dropdown&gt;'>
<dt-dropdown content-mode="invert" navigation-type="arrow-keys" placement="bottom-start">
  <template #anchor="{ attrs }">
    <dt-button v-bind="attrs" :size="200" kind="muted" importance="outlined">
      Inverted Dropdown
      <template #endIcon="{ iconSize }">
        <dt-icon name="chevron-down" :size="iconSize" />
      </template>
    </dt-button>
  </template>
  <template #list="{ close }">
    <dt-list-item role="menuitem" @click="close">Option 1</dt-list-item>
    <dt-list-item role="menuitem" @click="close">Option 2</dt-list-item>
    <dt-list-item role="menuitem" @click="close">Option 3</dt-list-item>
  </template>
</dt-dropdown>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="dropdown" :also-import="['dropdownseparator']" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A screen reader visible only close button is added by default.</p>
<p class="d-docsite--paragraph">The dropdown menu has a role of &quot;menu&quot; and the list items have a role &quot;menuitem&quot;. See <a href="https://www.w3.org/WAI/ARIA/apg/#menubutton" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">W3C guidelines</a> for more information.</p>
<h3 id="list" tabindex="-1" class="d-docsite--header-3">List <a class="header-anchor d-link d-docsite--link d-link" href="#list" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The Dropdown is rendered with semantic <code v-pre>&lt;ul&gt;</code> and <code v-pre>&lt;li&gt;</code>. The list slot is wrapped in <code v-pre>&lt;ul&gt;</code> element so make sure the items are wrapped in <code v-pre>&lt;li&gt;</code> element and each item has the menuitem role. It is recommended to use the <RouteLink to="/components/list-item.html" class="d-docsite--link d-link">List Item component</RouteLink> as it supports all the necessary accessibility props and interactions.</p>
<h3 id="focus-keyboard" tabindex="-1" class="d-docsite--header-3">Focus &amp; Keyboard <a class="header-anchor d-link d-docsite--link d-link" href="#focus-keyboard" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">A dropdown menu has well defined standard <a href="https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/examples/menu-button-links/#kbd_label" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">keyboard interactions</a> that it should support. The focus will be set to the the active item on keyboard navigation. <code v-pre>UP</code> and <code v-pre>DOWN</code> arrows move the highlighted selection between the list items. <code v-pre>HOME</code> and <code v-pre>END</code> keys will jump straight to the first or the last item. Once the beginning or the end of the list is reached the selection will move to the opposite end of the list by default. If you want custom behavior you can pass <code v-pre>onBeginningOfList</code> and <code v-pre>onEndOfList</code> methods as props. <code v-pre>A-Z</code> keys will cycle through items that start with the corresponding letter.</p>
<p class="d-docsite--paragraph">Pressing <code v-pre>ESC</code> key while the dropdown has focus will emit an <strong>escape</strong> event and will close the menu. When the <strong>highlight</strong> selection changes, a highlight event is emitted with the index of the currently highlighted item. <code v-pre>SPACE</code> key will open the dropdown if it's closed and select the item when it's pressed on a list item.</p>
</div></template>


<script setup>
const items = [
  { name: 'Menu item 1', id: 1 },
  { name: 'Menu item 2', id: 2 },
  { name: 'Another menu item 1', id: 3 },
  { name: 'Menu item 3', id: 4 },
  { name: 'Another menu item 2', id: 5 },
];
</script>